import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DeleteButtonWithConfirmation } from 'components/Common/Button/DeleteButtonWithConfirmation'
import { MoneyInput } from 'components/Common/Input/MoneyInput'
import { JsonViewer } from 'components/Common/JsonViewer'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { useAppController } from 'customHooks/useAppController'
import { bomItemQuantitySelector } from 'features/BillOfMaterials/store/selectors/bomItemQuantitySelector'
import { bomItemSelector } from 'features/BillOfMaterials/store/selectors/bomItemSelector'
import { projectIdSelector } from 'features/BillOfMaterials/store/selectors/projectIdSelector'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { RequestForQuotationController } from 'features/RequestForQuotations/RFQ/RequestForQuotationsController'
import { isEqual } from 'lodash'
import {
  AssemblyHeaderRow,
  BomItemType,
  PartTypeRow,
} from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  MoneyDto,
  Quotation,
  QuotationLineDto,
  RequestForQuotationDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import store, { RootAppState, useAppSelector } from 'store/configureStore'
import { useShowException } from 'utils/useShowException'
import { ProjectQuotationsController } from '../ProjectQuotationController'

const quotationResponseColorMap: Record<
  Quotation['state'],
  {
    color:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'error'
      | 'info'
      | 'success'
      | 'warning'
    variant: 'filled' | 'outlined'
  }
> = {
  New: { color: 'warning', variant: 'filled' },
  Cancelled: { color: 'error', variant: 'filled' },
  QuotationResponseAcceptedByBuyer: { color: 'success', variant: 'filled' },
  QuotationResponseCreatedBySupplier: { color: 'info', variant: 'filled' },
  QuotationResponseRejectedByBuyer: { color: 'error', variant: 'filled' },
  QuotationResponseMergedIntoProject: { color: 'default', variant: 'filled' },
  QuotationResponseMergeError: { color: 'default', variant: 'filled' },
  QuotationResponseReceivedByBuyer: { color: 'default', variant: 'filled' },
}

export type ManageQuotationResponseDialogProps = {
  isOpen: boolean
  onClose: () => void
}

type RfqBomItem = NonNullable<
  RequestForQuotationDto['bomItemsAndActivities']
>[number]

type LocalResponsePriceMap = Record<string, MoneyDto>
type LocalLineCommentMap = Record<string, string>

function formatMoney(money: MoneyDto | null | undefined): string {
  if (!money?.value && money?.value !== 0) {
    return '-'
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.value)
  } catch {
    return `${money.value} ${money.currency || ''}`.trim()
  }
}

function getBomItemType(
  state: RootAppState,
  bomItemId: string,
): BomItemType | null {
  if (!bomItemId) {
    return null
  }

  if (state.project.partTypes?.[bomItemId]) {
    return BomItemType.partType
  }

  if (state.project.partInstances?.[bomItemId]) {
    return BomItemType.partInstance
  }

  if (state.project.assemblyHeaders?.[bomItemId]) {
    return BomItemType.assemblyType
  }

  if (state.project.assemblyInstances?.[bomItemId]) {
    return BomItemType.assemblyInstance
  }

  return null
}

const QuotationResponseItemRow = ({
  rfqItem,
  responsePrice,
  lineComment,
  onResponsePriceChange,
  onLineCommentChange,
}: {
  rfqItem: RfqBomItem
  responsePrice: MoneyDto | null
  lineComment: string
  onResponsePriceChange: (bomItemId: string, price: MoneyDto | null) => void
  onLineCommentChange: (bomItemId: string, comment: string) => void
}) => {
  const getLocalizedWorkingStepLabel = useLocalizedWorkingStepLabel()

  const bomItemType = useAppSelector((state: RootAppState) =>
    getBomItemType(state, rfqItem.bomItemId),
  )

  const bomItemPointer = useMemo<BomItemPointer | null>(() => {
    if (!bomItemType) {
      return null
    }

    return {
      id: rfqItem.bomItemId,
      type: bomItemType,
    }
  }, [bomItemType, rfqItem.bomItemId])

  const bomItem = useAppSelector((state: RootAppState) => {
    if (!bomItemPointer) {
      return null
    }

    return bomItemSelector(state, bomItemPointer)
  }, isEqual) as AssemblyHeaderRow | PartTypeRow | null

  const currentPrice = useAppSelector((state: RootAppState) => {
    if (!bomItemPointer) {
      return null
    }

    return projectSelectors.bomItemFinancialSelector(
      bomItemPointer,
      'salesPricePerItem',
    )(state)
  }, isEqual) as MoneyDto | null

  const bomItemQuantity = useAppSelector((state: RootAppState) => {
    if (!bomItemPointer) {
      return null
    }

    return bomItemQuantitySelector(bomItemPointer)(state)
  }, isEqual)

  const thumbnailSrc = useBomItemThumbnailImageSrc(
    bomItemPointer || { id: '', type: BomItemType.partType },
  )

  const itemName =
    bomItem?.name || bomItem?.originalFileName || rfqItem.bomItemId

  return (
    <TableRow
      sx={{
        borderLeft: (theme) =>
          bomItemPointer
            ? `3px solid transparent`
            : `3px solid ${theme.palette.warning.light}`,
        '&:last-child td': { borderBottom: 0 },
        '&:hover': {
          backgroundColor: (theme) => theme.palette.action.selected,
        },
      }}
    >
      <TableCell sx={{ py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {thumbnailSrc.imageSrc ? (
            <Box
              component="img"
              src={thumbnailSrc.imageSrc}
              alt={itemName}
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                backgroundColor: (theme) => theme.palette.action.hover,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
                fontSize: 10,
                flexShrink: 0,
              }}
            >
              N/A
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 200,
              }}
            >
              {itemName}
            </Typography>
            {/* <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                fontSize: '0.65rem',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 240,
              }}
            >
              {rfqItem.bomItemId}
            </Typography> */}
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={{ py: 1.25 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {(rfqItem.activities ?? []).length > 0 ? (
            (rfqItem.activities ?? []).map((activity, index) => (
              <WorkingStepIconSvg
                key={activity.workingStepType}
                workingStepType={activity.workingStepType}
                attributes={{ width: '32px' }}
              />
              // <Chip
              //   key={`${rfqItem.bomItemId}-${activity.workingStepType}-${index}`}
              //   label={
              //     activity.workingStepType
              //       ? getLocalizedWorkingStepLabel(
              //           activity.workingStepType as WorkingStepType,
              //         )
              //       : 'Activity'
              //   }
              //   size="small"
              //   variant="outlined"
              //   sx={(theme) => ({
              //     height: 22,
              //     fontSize: '0.75rem',
              //     backgroundColor: theme.palette.background.paper,
              //     borderColor: theme.palette.divider,
              //   })}
              // />
            ))
          ) : (
            <Typography variant="body2" color="text.disabled">
              -
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ py: 1.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {bomItemQuantity ?? '-'}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ py: 1.25 }}>
        <Typography variant="body2" color="text.secondary">
          {formatMoney(currentPrice)}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ minWidth: 180, py: 1.25 }}>
        {bomItemPointer ? (
          <Box sx={{ maxWidth: 180, ml: 'auto' }}>
            <MoneyInput
              defaultMoneyObject={responsePrice}
              onChange={(event) => {
                const moneyDto = event.currentTarget.value
                onResponsePriceChange(rfqItem.bomItemId, moneyDto)
              }}
              value={responsePrice}
            />
          </Box>
        ) : (
          <Chip
            label="Not in project"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: 24 }}
          />
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 240, py: 1.25 }}>
        <TextField
          size="small"
          variant="standard"
          placeholder="Line note..."
          value={lineComment}
          onChange={(e) =>
            onLineCommentChange(rfqItem.bomItemId, e.target.value)
          }
          fullWidth
          multiline
          maxRows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8rem',
              backgroundColor: 'background.paper',
            },
          }}
        />
      </TableCell>
    </TableRow>
  )
}

export const ManageQuotationResponseDialog = ({
  isOpen,
  onClose,
}: ManageQuotationResponseDialogProps) => {
  const projectId = useAppSelector(projectIdSelector)
  const rfqMetadata = useAppSelector(
    (state) => state.project.activeProject?.rfqMetadata,
  )

  const { controller: rfqController, loading: rfqLoading } = useAppController(
    () => new RequestForQuotationController(),
  )
  const { controller: quotationController, loading: quotationLoading } =
    useAppController(() => new ProjectQuotationsController())

  const ShowException = useShowException('quotation response')
  const [rfq, setRfq] = useState<RequestForQuotationDto | null>(null)
  const [localResponsePrices, setLocalResponsePrices] =
    useState<LocalResponsePriceMap>({})
  const [localLineComments, setLocalLineComments] =
    useState<LocalLineCommentMap>({})

  // Quotation-level editable fields
  const [quotationComment, setQuotationComment] = useState('')
  const [quotationReference, setQuotationReference] = useState('')
  const [quotationOrderNumber, setQuotationOrderNumber] = useState('')
  const [quotationDeliveryTermsCode, setQuotationDeliveryTermsCode] =
    useState('')
  const [quotationPaymentTermsCode, setQuotationPaymentTermsCode] = useState('')
  const [quotationExpireDate, setQuotationExpireDate] = useState('')
  const [quotationValidityDays, setQuotationValidityDays] = useState<
    number | undefined
  >(undefined)

  useEffect(() => {
    if (!isOpen || !rfqMetadata?.createdFromRequestForQuotationId) {
      return undefined
    }

    let isMounted = true

    const fetchRfq = async () => {
      try {
        const response = await rfqController.GetRfqById(
          rfqMetadata.createdFromRequestForQuotationId,
        )

        if (isMounted) {
          setRfq(response)
        }
      } catch (error) {
        ShowException(error)
      }
    }

    fetchRfq()

    return () => {
      isMounted = false
    }
  }, [
    isOpen,
    rfqController,
    rfqMetadata?.createdFromRequestForQuotationId,
    ShowException,
  ])

  const sendDisabled =
    !projectId ||
    !rfqMetadata?.forwardId ||
    !rfqMetadata?.forwardSender ||
    quotationLoading[`PostQuotationResponseToSCSN-${projectId}`]

  const rfqItems = useMemo(() => rfq?.bomItemsAndActivities ?? [], [rfq])

  const rfqItemCurrentPrices = useAppSelector(
    useCallback(
      (state: RootAppState) => {
        const pricesByBomItemId: LocalResponsePriceMap = {}

        for (const item of rfqItems) {
          const bomItemType = getBomItemType(state, item.bomItemId)
          if (!bomItemType) {
            continue
          }

          const bomItemPointer: BomItemPointer = {
            id: item.bomItemId,
            type: bomItemType,
          }

          const currentPrice = projectSelectors.bomItemFinancialSelector(
            bomItemPointer,
            'salesPricePerItem',
          )(state)

          if (currentPrice) {
            pricesByBomItemId[item.bomItemId] = currentPrice
          }
        }

        return pricesByBomItemId
      },
      [rfqItems],
    ),
    isEqual,
  )

  const [quotationResponse, setQuotationResponse] = useState<Quotation | null>(
    null,
  )

  const fetchQuotationResponse = useCallback(async () => {
    try {
      const resp = await quotationController.GetQuotationResponse()
      const q = resp.quotation
      setQuotationResponse(q)
      if (q) {
        setQuotationComment(q.comment ?? '')
        setQuotationReference(q.reference ?? '')
        setQuotationOrderNumber(q.orderNumber ?? '')
        setQuotationDeliveryTermsCode(q.deliveryTermsCode ?? '')
        setQuotationPaymentTermsCode(q.paymentTermsCode ?? '')
        setQuotationExpireDate(
          q.quotationExpireDate
            ? new Date(q.quotationExpireDate).toISOString().split('T')[0]
            : '',
        )
        setQuotationValidityDays(q.validityPeriodInDays ?? undefined)
      }
    } catch (err) {
      ShowException(err)
    }
  }, [quotationController, ShowException])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    fetchQuotationResponse()

    setLocalResponsePrices((previous) => {
      const initialPrices: LocalResponsePriceMap = {}

      for (const item of rfqItems) {
        const existing = previous[item.bomItemId]
        if (existing) {
          initialPrices[item.bomItemId] = existing
          continue
        }

        const current = rfqItemCurrentPrices[item.bomItemId]
        if (current) {
          initialPrices[item.bomItemId] = current
        }
      }

      return isEqual(previous, initialPrices) ? previous : initialPrices
    })
  }, [isOpen, rfqItems, rfqItemCurrentPrices, fetchQuotationResponse])

  const handleLocalPriceChange = useCallback(
    (bomItemId: string, price: MoneyDto | null) => {
      setLocalResponsePrices((previous) => {
        if (!price) {
          const { [bomItemId]: _removed, ...rest } = previous
          return rest
        }

        return {
          ...previous,
          [bomItemId]: price,
        }
      })
    },
    [],
  )

  const handleLineCommentChange = useCallback(
    (bomItemId: string, comment: string) => {
      setLocalLineComments((previous) => ({
        ...previous,
        [bomItemId]: comment,
      }))
    },
    [],
  )

  const handleCreate = async () => {
    if (!projectId || !rfqMetadata?.forwardId || !rfqMetadata?.forwardSender) {
      return
    }

    const quotationResponsePrices: Array<QuotationLineDto> = []

    for (const item of rfqItems) {
      const localPrice = localResponsePrices[item.bomItemId]
      if (!localPrice || localPrice.value === undefined) {
        continue
      }

      const bomItem = bomItemSelector(store.getState(), {
        id: item.bomItemId,
        type: undefined,
      }) as AssemblyHeaderRow | PartTypeRow

      quotationResponsePrices.push({
        id: bomItem.id,
        comment:
          localLineComments[item.bomItemId] ||
          bomItem.comment ||
          'Price provided in quotation response',
        salesPrice: localPrice,
        name: bomItem.name,
        quantity: bomItem.financial.totalProjectQuantity,
      })
    }

    try {
      await quotationController.CreateQuotationResponse({
        forwardId: rfqMetadata.forwardSender,
        forwardSender: rfqMetadata.forwardId,
        lines: quotationResponsePrices,
        comment: quotationComment || undefined,
        reference: quotationReference || undefined,
        orderNumber: quotationOrderNumber || undefined,
        deliveryTermsCode: quotationDeliveryTermsCode || undefined,
        paymentTermsCode: quotationPaymentTermsCode || undefined,
        quotationExpireDate: quotationExpireDate
          ? new Date(quotationExpireDate)
          : undefined,
        validityPeriodInDays: quotationValidityDays,
      })

      onClose()
    } catch (error) {
      ShowException(error)
    }
  }

  const handleSave = async () => {
    if (!quotationResponse) {
      return
    }

    const quotationResponsePrices: Array<QuotationLineDto> = []

    for (const item of rfqItems) {
      const localPrice = localResponsePrices[item.bomItemId]
      if (!localPrice || localPrice.value === undefined) {
        continue
      }

      const bomItem = bomItemSelector(store.getState(), {
        id: item.bomItemId,
        type: undefined,
      }) as AssemblyHeaderRow | PartTypeRow | null

      if (!bomItem) {
        continue
      }

      quotationResponsePrices.push({
        id: bomItem.id,
        comment:
          localLineComments[item.bomItemId] ||
          bomItem.comment ||
          'Price provided in quotation response',
        salesPrice: localPrice,
        name: bomItem.name,
        quantity: bomItem.financial.totalProjectQuantity,
      })
    }

    try {
      var saveQuotationResp = await quotationController.SaveQuotationResponse({
        quotationResponseId: quotationResponse.id,
        lines: quotationResponsePrices,
        comment: quotationComment || undefined,
        reference: quotationReference || undefined,
        deliveryTermsCode: quotationDeliveryTermsCode || undefined,
        paymentTermsCode: quotationPaymentTermsCode || undefined,
        validityPeriodInDays: quotationValidityDays ?? 0,
      })

      setQuotationResponse(saveQuotationResp.quotation)
    } catch (error) {
      ShowException(error)
    }
  }

  const handleSend = async (quotationId: string) => {
    try {
      await quotationController.SendQuotationResponseToBuyer(quotationId)
    } catch (err) {
      ShowException(err)
    }
  }

  const handleDelete = async (quotationId: string) => {
    try {
      await quotationController.DeleteQuotationResponse(quotationId)
    } catch (err) {
      ShowException(err)
    }
  }

  const [detailsExpanded, setDetailsExpanded] = useState(true)

  const pricedItemsCount = useMemo(
    () =>
      rfqItems.filter(
        (item) => localResponsePrices[item.bomItemId]?.value !== undefined,
      ).length,
    [localResponsePrices, rfqItems],
  )

  const completedDetailFieldsCount = [
    quotationOrderNumber,
    quotationReference,
    quotationDeliveryTermsCode,
    quotationPaymentTermsCode,
    quotationExpireDate,
    quotationValidityDays,
    quotationComment,
  ].filter(Boolean).length

  const detailFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.paper',
    },
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 1.5,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, letterSpacing: -0.2 }}
            >
              Quotation Response
            </Typography>
            {quotationResponse && (
              <Chip
                label={quotationResponse.state}
                color={quotationResponseColorMap[quotationResponse.state].color}
                variant={
                  quotationResponseColorMap[quotationResponse.state].variant
                }
                size="small"
              />
            )}
          </Box>
        </Box>
        {quotationResponse && (
          <DeleteButtonWithConfirmation
            color="error"
            buttonType="button"
            onDelete={() => handleDelete(quotationResponse.id)}
            muiButtonProps={{
              size: 'small',
              disabled: sendDisabled,
              startIcon: quotationLoading[
                `DeleteQuotationResponse-${projectId}`
              ] ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined,
            }}
          />
        )}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0, backgroundColor: 'background.default' }}>
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          {!rfqMetadata?.forwardId || !rfqMetadata?.forwardSender ? (
            <Alert severity="warning" variant="outlined">
              Missing RFQ routing metadata. Cannot send from this project.
            </Alert>
          ) : null}
        </Box>

        <Box sx={{ px: 3, pb: 2 }}>
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: (theme) => theme.shadows[1],
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                px: 2,
                py: 1.5,
                backgroundColor: 'background.paper',
              }}
              onClick={() => setDetailsExpanded((prev) => !prev)}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Quotation Details
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Header fields sent with quotation response
                </Typography>
              </Box>
              <Chip
                label={`${completedDetailFieldsCount}/7`}
                size="small"
                variant="outlined"
                sx={{ mr: 1, backgroundColor: 'background.paper' }}
              />
              <Tooltip title={detailsExpanded ? 'Collapse' : 'Expand'}>
                <IconButton size="small">
                  {detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            <Collapse in={detailsExpanded}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                  },
                  gap: 1.5,
                  p: 2,
                }}
              >
                <TextField
                  size="small"
                  variant="standard"
                  label="Order Number"
                  value={quotationOrderNumber}
                  onChange={(e) => setQuotationOrderNumber(e.target.value)}
                  sx={detailFieldSx}
                />
                <TextField
                  size="small"
                  variant="standard"
                  label="Reference"
                  value={quotationReference}
                  onChange={(e) => setQuotationReference(e.target.value)}
                  sx={detailFieldSx}
                />
                <TextField
                  size="small"
                  variant="standard"
                  label="Delivery Terms"
                  value={quotationDeliveryTermsCode}
                  onChange={(e) =>
                    setQuotationDeliveryTermsCode(e.target.value)
                  }
                  placeholder="e.g. EXW, DAP"
                  sx={detailFieldSx}
                />
                <TextField
                  size="small"
                  variant="standard"
                  label="Payment Terms"
                  value={quotationPaymentTermsCode}
                  onChange={(e) => setQuotationPaymentTermsCode(e.target.value)}
                  placeholder="e.g. NET30"
                  sx={detailFieldSx}
                />
                <TextField
                  size="small"
                  variant="standard"
                  label="Expiry Date"
                  type="date"
                  value={quotationExpireDate}
                  onChange={(e) => setQuotationExpireDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={detailFieldSx}
                />
                <TextField
                  size="small"
                  variant="standard"
                  label="Validity (days)"
                  type="number"
                  value={quotationValidityDays ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    setQuotationValidityDays(
                      val === '' ? undefined : parseInt(val, 10),
                    )
                  }}
                  slotProps={{
                    htmlInput: { min: 0 },
                  }}
                  sx={detailFieldSx}
                />
                <TextField
                  size="small"
                  variant="standard"
                  label="Comment"
                  value={quotationComment}
                  onChange={(e) => setQuotationComment(e.target.value)}
                  multiline
                  minRows={2}
                  maxRows={4}
                  sx={{ ...detailFieldSx, gridColumn: '1 / -1' }}
                  placeholder="Additional notes for the buyer..."
                />
              </Box>
            </Collapse>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Line Items
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Set response prices and optional notes per requested item
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip
                label={`${rfqItems.length} items`}
                size="small"
                variant="outlined"
                sx={{ backgroundColor: 'background.paper' }}
              />
              <Chip
                label={`${pricedItemsCount} priced`}
                size="small"
                color={pricedItemsCount > 0 ? 'success' : 'default'}
                variant="outlined"
                sx={{ backgroundColor: 'background.paper' }}
              />
            </Box>
          </Box>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              mb: 1.5,
              borderRadius: 1.5,
              backgroundColor: 'background.paper',
            }}
          >
            Response prices are local and do not update BOM financials.
          </Alert>

          {rfqLoading[
            `GetRfqById-${rfqMetadata?.createdFromRequestForQuotationId}`
          ] ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rfqItems.length > 0 ? (
            <TableContainer
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: (theme) => theme.shadows[1],
                maxHeight: 420,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        backgroundColor: (theme) => theme.palette.grey[50],
                        borderBottom: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      Item
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      Activities
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                      Qty
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                      Current price
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                      Response price
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      Comment
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfqItems.map((item, index) => (
                    <QuotationResponseItemRow
                      key={`${item.bomItemId}-${index}`}
                      rfqItem={item}
                      responsePrice={
                        localResponsePrices[item.bomItemId] || null
                      }
                      lineComment={localLineComments[item.bomItemId] ?? ''}
                      onResponsePriceChange={handleLocalPriceChange}
                      onLineCommentChange={handleLineCommentChange}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="warning" variant="outlined">
              No RFQ items found for this project.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <JsonViewer data={localResponsePrices} />

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          gap: 1,
          backgroundColor: 'background.paper',
          boxShadow: (theme) => `0 -8px 20px ${theme.palette.action.hover}`,
        }}
      >
        <Button onClick={onClose} size="small">
          Close
        </Button>
        {!quotationResponse && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={sendDisabled}
            size="small"
            startIcon={
              quotationLoading[`PostQuotationResponseToSCSN-${projectId}`] ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            Create quotation response
          </Button>
        )}
        {quotationResponse && (
          <Button
            variant="contained"
            onClick={() => handleSend(quotationResponse.id)}
            disabled={sendDisabled}
            size="small"
            startIcon={
              quotationLoading[`SendQuotationResponseToBuyer-${projectId}`] ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            Send quotation response to buyer
          </Button>
        )}
        {quotationResponse && (
          <Button variant="outlined" onClick={handleSave} size="small">
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
